import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import { PiCaretDown, PiCrop, PiCube, PiDotOutline } from 'react-icons/pi';
import {
  CHIP_MENU_ALIGN_LEFT,
  ChipMenu,
  TOOLBAR_DENSITY_REGULAR,
  TOOLBAR_TONE_SURFACE,
  TOOLBAR_VARIANT_FLOATING,
  Toolbar,
} from '@aibuild/ui';
import type { DisplayMode } from '../stage/displayModes';
import type { SelectionMode } from '../viewerSettings';
import { usePortalContainer } from '../environment';
import ChromeButton from './ChromeButton';
import PillTrigger from './PillTrigger';
import { DISPLAY_MODES, displayModeLabel } from './displayModes';
import { messages } from './messages';
import { usePopover } from './usePopover';
import { TopSlot } from './TopBar.styled';

const SELECTION_MODES: readonly { mode: SelectionMode; icon: React.ReactNode; label: keyof typeof messages }[] = [
  { mode: 'body', icon: <PiCube />, label: 'selectBodies' },
  { mode: 'vertex', icon: <PiDotOutline />, label: 'selectVertices' },
];

export interface TopBarProps {
  displayMode: DisplayMode;
  onDisplayModeChange: (mode: DisplayMode) => void;
  selectionMode: SelectionMode;
  onSelectionModeChange: (mode: SelectionMode) => void;
  /** Why selecting and cutting are unavailable, when they are. */
  selectionUnavailable?: string;
  sectionUnavailable?: string;
  sectionOn: boolean;
  onToggleSection: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  displayMode,
  onDisplayModeChange,
  selectionMode,
  onSelectionModeChange,
  selectionUnavailable,
  sectionUnavailable,
  sectionOn,
  onToggleSection,
}) => {
  const intl = useIntl();
  const portal = usePortalContainer();
  const shadingRef = useRef<HTMLButtonElement>(null);
  const shading = usePopover(shadingRef);
  const shadingLabel = intl.formatMessage(messages.shading);
  return (
    <TopSlot>
      <Toolbar
        variant={TOOLBAR_VARIANT_FLOATING}
        tone={TOOLBAR_TONE_SURFACE}
        density={TOOLBAR_DENSITY_REGULAR}
        ariaLabel={intl.formatMessage(messages.topBar)}
        dataTestId="model-viewer__top-bar"
      >
        <Toolbar.Group>
          <PillTrigger
            ref={shadingRef}
            label={intl.formatMessage(displayModeLabel(displayMode))}
            ariaLabel={shadingLabel}
            caret={<PiCaretDown />}
            open={shading.open}
            onClick={shading.toggle}
            onMouseDown={shading.keepOpenOnPress}
            testId="model-viewer__shading"
          />
          {shading.open && (
            <ChipMenu
              onClose={shading.close}
              portal
              portalContainer={portal}
              anchorRef={shadingRef}
              align={CHIP_MENU_ALIGN_LEFT}
              ariaLabel={shadingLabel}
              dataTestId="model-viewer__shading-menu"
            >
              <ChipMenu.List>
                {DISPLAY_MODES.map(({ mode, label }) => (
                  <ChipMenu.Item
                    key={mode}
                    isActive={mode === displayMode}
                    onClick={() => {
                      onDisplayModeChange(mode);
                      shading.close();
                    }}
                    dataTestId={`model-viewer__shading-${mode}`}
                  >
                    {intl.formatMessage(label)}
                  </ChipMenu.Item>
                ))}
              </ChipMenu.List>
            </ChipMenu>
          )}
        </Toolbar.Group>
        <Toolbar.Group>
          {SELECTION_MODES.map(({ mode, icon, label }) => (
            <ChromeButton
              key={mode}
              label={intl.formatMessage(messages[label])}
              icon={icon}
              pressed={selectionMode === mode && !selectionUnavailable}
              disabledReason={selectionUnavailable}
              onClick={() => onSelectionModeChange(mode)}
              testId={`model-viewer__select-${mode}`}
            />
          ))}
        </Toolbar.Group>
        <Toolbar.Group>
          <ChromeButton
            label={intl.formatMessage(messages.section)}
            icon={<PiCrop />}
            pressed={sectionOn}
            disabledReason={sectionUnavailable}
            onClick={onToggleSection}
            testId="model-viewer__section"
          />
        </Toolbar.Group>
      </Toolbar>
    </TopSlot>
  );
};

export default TopBar;
